// UsersTable.tsx
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { connect } from "react-redux";

import type { SortKey, UsersTableProps } from "../../types";
import { RootState } from "../../../../store";
import { API_ENDPOINTS, REGEXES } from "../../../../constants/common";
import { apiClient } from "../../../../utils";

import { AdminActionCreator } from "../../../../store/Admin/actions";
import {
    selectSubscriptionPlansList,
    selectSubscriptionPlansPending,
    selectSubscriptionPlansError,

    // update user
    selectUpdateUserPending,
    selectUpdateUserError,
    selectUpdateUserErrorMessage,

    // reset password
    selectResetUserPasswordPending,
    selectResetUserPasswordError,
    selectResetUserPasswordErrorMessage,

    // delete user
    selectDeleteUserPending,
    selectDeleteUserError,
    selectDeleteUserErrorMessage,

    // set subscription
    selectSetUserSubscriptionPending,
    selectSetUserSubscriptionError,
    selectSetUserSubscriptionErrorMessage,

    // cancel subscription
    selectCancelUserSubscriptionPending,
    selectCancelUserSubscriptionError,
    selectCancelUserSubscriptionErrorMessage,
} from "../../../../store/selectors";

import { UserActionCreator } from "../../../../store/userActions";

import { useUsersTableMobileOptions } from "../../hooks/useUsersTableMobileOptions";
import { MobileSortBar } from "./MobileSortBar";

import MobileView from "./MobileView";
import DesktopView from "./DesktopView";
import DeleteUserModal from "./DeleteUserModal";
import CreateSubscriptionModal from "./CreateSubscriptionModal";
import EditUserModal from "./EditUserModal";
import ResetUserPasswordModal from "./ResetUserPasswordModal";

const EXPIRING_DAYS = 7;
const normalizeEmail = (value: unknown) => String(value || "").trim().toLowerCase();

type StateProps = {
    subscriptionPlansList: any[];
    subscriptionPlansPending?: boolean;
    subscriptionPlansError?: boolean;

    // update user
    updateUserPending?: boolean;
    updateUserError?: boolean;
    updateUserErrorMessage?: string | null;

    // reset password
    resetUserPasswordPending?: boolean;
    resetUserPasswordError?: boolean;
    resetUserPasswordErrorMessage?: string | null;

    // delete user
    deleteUserPending?: boolean;
    deleteUserError?: boolean;
    deleteUserErrorMessage?: string | null;

    // set subscription
    setUserSubscriptionPending?: boolean;
    setUserSubscriptionError?: boolean;
    setUserSubscriptionErrorMessage?: string | null;

    // cancel subscription
    cancelUserSubscriptionPending?: boolean;
    cancelUserSubscriptionError?: boolean;
    cancelUserSubscriptionErrorMessage?: string | null;
};

type DispatchProps = {
    subscriptionPlans: () => void;
    deleteUserRequest: (payload: { userId: number | string }) => void;

    setUserSubscriptionRequest: (payload: {
        userId: number | string;
        planId: number | string;
        expiresAt: string;
        planCode?: string | null;
    }) => void;

    cancelUserSubscriptionRequest: (payload: { userId: number | string }) => void;

    updateUserRequest: (payload: {
        userId: number | string;
        payload: {
            password?: string;
            name?: string;
            color?: string;
            telegram?: string;
            whatsapp?: string;
            instagram?: string;
            phone?: number | string;
        };
    }) => void;

    resetUserPasswordRequest: (payload: { email: string }) => void;

    // Notification
    setNotification: (payload: any) => void;
};

type Props = UsersTableProps & StateProps & DispatchProps;

const getUserLabel = (u: any) => u?.email || u?.name || `ID: ${u?.id ?? "—"}`;

const _UsersTable: React.FC<Props> = ({
                                          variant,
                                          title,
                                          users,
                                          total,
                                          pending,

                                          sortKey,
                                          sortDir,
                                          onChangeSortKey,
                                          onToggleDir,

                                          actionsDisabled = false,

                                          // from connect
                                          subscriptionPlans,
                                          subscriptionPlansList,
                                          subscriptionPlansPending,
                                          subscriptionPlansError,

                                          updateUserPending,
                                          updateUserError,
                                          updateUserErrorMessage,

                                          resetUserPasswordPending,
                                          resetUserPasswordError,
                                          resetUserPasswordErrorMessage,

                                          deleteUserPending,
                                          deleteUserError,
                                          deleteUserErrorMessage,

                                          setUserSubscriptionPending,
                                          setUserSubscriptionError,
                                          setUserSubscriptionErrorMessage,

                                          cancelUserSubscriptionPending,
                                          cancelUserSubscriptionError,
                                          cancelUserSubscriptionErrorMessage,

                                          deleteUserRequest,
                                          setUserSubscriptionRequest,
                                          cancelUserSubscriptionRequest,
                                          updateUserRequest,
                                          resetUserPasswordRequest,

                                          setNotification,
                                      }) => {
    useEffect(() => {
        if (!subscriptionPlansList?.length) subscriptionPlans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---------------- helpers: notifications ----------------
    const notifySuccess = (description: string) => {
        setNotification({
            isOpen: true,
            value: {
                title: "Готово",
                description,
                closeIcon: true,
                button: {
                    title: "Ок",
                    onClick: () => setNotification({ isOpen: false }),
                },
            },
        });
    };

    const notifyError = (description: string) => {
        setNotification({
            isOpen: true,
            value: {
                title: "Ошибка",
                description,
                closeIcon: true,
                button: {
                    title: "Понял",
                    onClick: () => setNotification({ isOpen: false }),
                },
            },
        });
    };

    // ---------------- listing creation access ----------------
    const [listingAccessEmails, setListingAccessEmails] = useState<Set<string>>(new Set());
    const [listingAccessPending, setListingAccessPending] = useState(false);
    const [listingAccessLoaded, setListingAccessLoaded] = useState(false);
    const [listingAccessActionEmail, setListingAccessActionEmail] = useState<string | null>(null);

    const loadListingAccess = useCallback(async () => {
        if (variant !== "users") return;

        setListingAccessPending(true);
        try {
            const response = await apiClient.get(API_ENDPOINTS.classifiedsListingCreationWhitelist, {
                params: { limit: 500 },
            });
            const entries = Array.isArray(response?.data?.data) ? response.data.data : [];
            setListingAccessEmails(new Set(entries.map((item: any) => normalizeEmail(item?.email)).filter(Boolean)));
            setListingAccessLoaded(true);
        } catch (error: any) {
            notifyError(error?.response?.data?.error || "Не удалось загрузить доступ к созданию объявлений");
        } finally {
            setListingAccessPending(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variant]);

    useEffect(() => {
        if (variant === "users" && !listingAccessLoaded) {
            void loadListingAccess();
        }
    }, [listingAccessLoaded, loadListingAccess, variant]);

    const toggleListingAccess = async (user: any) => {
        const email = normalizeEmail(user?.email);
        if (!REGEXES.email.test(email)) {
            notifyError("У пользователя нет валидного email");
            return;
        }

        const isAllowed = listingAccessEmails.has(email);
        setListingAccessActionEmail(email);
        try {
            if (isAllowed) {
                await apiClient.delete(API_ENDPOINTS.classifiedsListingCreationWhitelistEntry(email));
                setListingAccessEmails((current) => {
                    const next = new Set(current);
                    next.delete(email);
                    return next;
                });
                notifySuccess(`Доступ к созданию объявлений отключён: ${email}`);
            } else {
                await apiClient.post(API_ENDPOINTS.classifiedsListingCreationWhitelist, { email });
                setListingAccessEmails((current) => new Set(current).add(email));
                notifySuccess(`Доступ к созданию объявлений включён: ${email}`);
            }
        } catch (error: any) {
            notifyError(error?.response?.data?.error || "Не удалось изменить доступ к созданию объявлений");
        } finally {
            setListingAccessActionEmail(null);
        }
    };

    // ---------------- delete ----------------
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteUser, setDeleteUser] = useState<any>(null);

    const [deleteAttempted, setDeleteAttempted] = useState(false);
    const [deleteLabel, setDeleteLabel] = useState<string>("");

    const askDelete = (u: any) => {
        setDeleteUser(u);
        setDeleteOpen(true);
    };

    const closeDelete = () => {
        if (deleteUserPending) return;
        setDeleteOpen(false);
        setDeleteUser(null);
    };

    const confirmDelete = () => {
        const id = deleteUser?.id;
        if (!id) return;

        setDeleteAttempted(true);
        setDeleteLabel(getUserLabel(deleteUser));

        deleteUserRequest({ userId: id });
        closeDelete();
    };

    useEffect(() => {
        if (!deleteAttempted) return;
        if (deleteUserPending) return;

        if (deleteUserError) {
            notifyError(deleteUserErrorMessage || "Не удалось удалить пользователя");
        } else {
            notifySuccess(`Пользователь удалён: ${deleteLabel}`);
        }

        setDeleteAttempted(false);
        setDeleteLabel("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteUserPending, deleteUserError, deleteUserErrorMessage, deleteAttempted]);

    // ---------------- subscription modal ----------------
    const [subOpen, setSubOpen] = useState(false);
    const [subUser, setSubUser] = useState<any>(null);

    const [setSubAttempted, setSetSubAttempted] = useState(false);
    const [setSubLabel, setSetSubLabel] = useState<string>("");
    const [setSubPlanLabel, setSetSubPlanLabel] = useState<string>("");

    const [cancelSubAttempted, setCancelSubAttempted] = useState(false);
    const [cancelSubLabel, setCancelSubLabel] = useState<string>("");

    const askSubscription = (u: any) => {
        setSubUser(u);
        setSubOpen(true);
    };

    const closeSubscription = () => {
        if (subscriptionPlansPending) return;
        setSubOpen(false);
        setSubUser(null);
    };

    // notify: set subscription
    useEffect(() => {
        if (!setSubAttempted) return;
        if (setUserSubscriptionPending) return;

        if (setUserSubscriptionError) {
            notifyError(setUserSubscriptionErrorMessage || "Не удалось оформить подписку");
        } else {
            const planText = setSubPlanLabel ? ` (${setSubPlanLabel})` : "";
            notifySuccess(`Подписка оформлена${planText} для: ${setSubLabel}`);
        }

        setSetSubAttempted(false);
        setSetSubLabel("");
        setSetSubPlanLabel("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        setUserSubscriptionPending,
        setUserSubscriptionError,
        setUserSubscriptionErrorMessage,
        setSubAttempted,
    ]);

    // notify: cancel subscription
    useEffect(() => {
        if (!cancelSubAttempted) return;
        if (cancelUserSubscriptionPending) return;

        if (cancelUserSubscriptionError) {
            notifyError(cancelUserSubscriptionErrorMessage || "Не удалось отменить подписку");
        } else {
            notifySuccess(`Подписка отменена для: ${cancelSubLabel}`);
        }

        setCancelSubAttempted(false);
        setCancelSubLabel("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        cancelUserSubscriptionPending,
        cancelUserSubscriptionError,
        cancelUserSubscriptionErrorMessage,
        cancelSubAttempted,
    ]);

    // ---------------- edit user ----------------
    const [editOpen, setEditOpen] = useState(false);
    const [editUser, setEditUser] = useState<any>(null);

    const [updateAttempted, setUpdateAttempted] = useState(false);
    const [updateLabel, setUpdateLabel] = useState<string>("");

    const askEdit = (u: any) => {
        setEditUser(u);
        setEditOpen(true);
    };

    const closeEdit = () => {
        if (updateUserPending) return;
        setEditOpen(false);
        setEditUser(null);
    };

    useEffect(() => {
        if (!updateAttempted) return;
        if (updateUserPending) return;

        if (updateUserError) {
            notifyError(updateUserErrorMessage || "Не удалось обновить профиль");
        } else {
            notifySuccess(`Профиль обновлён: ${updateLabel}`);
        }

        setUpdateAttempted(false);
        setUpdateLabel("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateUserPending, updateUserError, updateUserErrorMessage, updateAttempted]);

    // ---------------- reset password ----------------
    const [resetOpen, setResetOpen] = useState(false);
    const [resetUser, setResetUser] = useState<any>(null);

    const [resetAttempted, setResetAttempted] = useState(false);
    const [resetEmailForSuccess, setResetEmailForSuccess] = useState<string | null>(null);

    const askResetPassword = (u: any) => {
        setResetUser(u);
        setResetOpen(true);
        setResetAttempted(false);
        setResetEmailForSuccess(null);
    };

    const closeResetPassword = () => {
        if (resetUserPasswordPending) return;
        setResetOpen(false);
        setResetUser(null);
        setResetAttempted(false);
        setResetEmailForSuccess(null);
    };

    useEffect(() => {
        if (!resetOpen) return;
        if (!resetAttempted) return;
        if (resetUserPasswordPending) return;

        if (resetUserPasswordError) {
            notifyError(resetUserPasswordErrorMessage || "Не удалось отправить письмо для сброса пароля");
            return; // оставляем модалку открытой
        }

        const email = resetEmailForSuccess || resetUser?.email || "";
        notifySuccess(`Письмо для сброса пароля отправлено на ${email}`);
        closeResetPassword();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        resetUserPasswordPending,
        resetUserPasswordError,
        resetUserPasswordErrorMessage,
        resetAttempted,
        resetEmailForSuccess,
        resetOpen,
    ]);

    // ---------------- ui helpers ----------------
    const rangeText = useMemo(() => {
        const shown = users?.length ?? 0;
        if (typeof total !== "number") return `${shown} загружено`;
        return `${shown} из ${total}`;
    }, [total, users?.length]);

    const handleSortClick = (k: SortKey) => {
        if (k === sortKey) onToggleDir();
        else onChangeSortKey(k);
    };

    const mobileOptions = useUsersTableMobileOptions(variant);

    return (
        <section className="overflow-hidden border-y border-slate-200 bg-white sm:rounded-2xl sm:border">
            <div className="flex flex-col gap-2 border-b border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                <div className="min-w-0">
                    <div className="text-base font-semibold text-slate-900">{title}</div>
                    <div className="mt-1 text-xs text-slate-500">{rangeText}</div>
                </div>
                {pending ? <span className="text-xs font-medium text-slate-500">Обновляем…</span> : null}
            </div>

            {/* MOBILE sort bar */}
            <MobileSortBar
                options={mobileOptions}
                sortKey={sortKey}
                sortDir={sortDir}
                onSortKey={onChangeSortKey}
                onToggleDir={onToggleDir}
            />

            {/* MOBILE */}
            <MobileView
                variant={variant}
                users={users}
                pending={pending}
                actionsDisabled={actionsDisabled}
                expiringDays={EXPIRING_DAYS}
                listingAccessEmails={listingAccessEmails}
                listingAccessPending={listingAccessPending}
                listingAccessActionEmail={listingAccessActionEmail}
                onEditProfile={askEdit}
                onResetPassword={askResetPassword}
                onCreateSubscription={askSubscription}
                onToggleListingAccess={toggleListingAccess}
                onDeleteUser={askDelete}
            />

            {/* DESKTOP */}
            <DesktopView
                variant={variant}
                users={users}
                pending={pending}
                sortKey={sortKey}
                sortDir={sortDir}
                onSortClick={handleSortClick}
                actionsDisabled={actionsDisabled}
                expiringDays={EXPIRING_DAYS}
                listingAccessEmails={listingAccessEmails}
                listingAccessPending={listingAccessPending}
                listingAccessActionEmail={listingAccessActionEmail}
                onEditProfile={askEdit}
                onResetPassword={askResetPassword}
                onCreateSubscription={askSubscription}
                onToggleListingAccess={toggleListingAccess}
                onDeleteUser={askDelete}
            />

            <DeleteUserModal
                isOpen={deleteOpen}
                user={deleteUser}
                onClose={closeDelete}
                onConfirm={confirmDelete}
            />

            <CreateSubscriptionModal
                isOpen={subOpen}
                user={subUser}
                onClose={closeSubscription}
                onConfirm={({ planId, expiresAt }) => {
                    if (!subUser?.id) return;

                    const plan = subscriptionPlansList?.find((p: any) => String(p?.id) === String(planId));
                    const planCode = plan?.code ?? plan?.name ?? null;

                    setSetSubAttempted(true);
                    setSetSubLabel(getUserLabel(subUser));
                    setSetSubPlanLabel(String(planCode ?? "").trim());

                    setUserSubscriptionRequest({
                        userId: subUser.id,
                        planId,
                        expiresAt,
                        planCode,
                    });

                    closeSubscription();
                }}
                onCancel={({ userId }) => {
                    if (!userId) return;

                    setCancelSubAttempted(true);
                    setCancelSubLabel(getUserLabel(subUser));

                    cancelUserSubscriptionRequest({ userId });
                    closeSubscription();
                }}
                plans={subscriptionPlansList}
                plansPending={subscriptionPlansPending}
                plansError={subscriptionPlansError}
            />

            <EditUserModal
                isOpen={editOpen}
                user={editUser}
                pending={!!updateUserPending}
                error={!!updateUserError}
                errorMessage={updateUserErrorMessage ?? null}
                onClose={closeEdit}
                onConfirm={(payload) => {
                    if (!editUser?.id) return;

                    setUpdateAttempted(true);
                    setUpdateLabel(getUserLabel(editUser));

                    updateUserRequest({
                        userId: editUser.id,
                        payload,
                    });

                    closeEdit();
                }}
            />

            <ResetUserPasswordModal
                isOpen={resetOpen}
                user={resetUser}
                pending={!!resetUserPasswordPending}
                error={!!resetUserPasswordError}
                errorMessage={resetUserPasswordErrorMessage ?? null}
                onClose={closeResetPassword}
                onConfirm={({ email }) => {
                    if (!email) return;
                    setResetAttempted(true);
                    setResetEmailForSuccess(email);
                    resetUserPasswordRequest({ email });
                }}
            />
        </section>
    );
};

const mapStateToProps = (state: RootState) => ({
    subscriptionPlansList: selectSubscriptionPlansList(state),
    subscriptionPlansPending: selectSubscriptionPlansPending(state),
    subscriptionPlansError: selectSubscriptionPlansError(state),

    updateUserPending: selectUpdateUserPending(state),
    updateUserError: selectUpdateUserError(state),
    updateUserErrorMessage: selectUpdateUserErrorMessage(state),

    resetUserPasswordPending: selectResetUserPasswordPending(state),
    resetUserPasswordError: selectResetUserPasswordError(state),
    resetUserPasswordErrorMessage: selectResetUserPasswordErrorMessage(state),

    deleteUserPending: selectDeleteUserPending(state),
    deleteUserError: selectDeleteUserError(state),
    deleteUserErrorMessage: selectDeleteUserErrorMessage(state),

    setUserSubscriptionPending: selectSetUserSubscriptionPending(state),
    setUserSubscriptionError: selectSetUserSubscriptionError(state),
    setUserSubscriptionErrorMessage: selectSetUserSubscriptionErrorMessage(state),

    cancelUserSubscriptionPending: selectCancelUserSubscriptionPending(state),
    cancelUserSubscriptionError: selectCancelUserSubscriptionError(state),
    cancelUserSubscriptionErrorMessage: selectCancelUserSubscriptionErrorMessage(state),
});

const mapDispatchToProps = {
    subscriptionPlans: UserActionCreator.getSubscriptionPlans,
    deleteUserRequest: AdminActionCreator.deleteUser,
    setUserSubscriptionRequest: AdminActionCreator.setUserSubscription,
    cancelUserSubscriptionRequest: AdminActionCreator.cancelUserSubscription,
    updateUserRequest: AdminActionCreator.updateUser,
    resetUserPasswordRequest: AdminActionCreator.resetUserPassword,

    setNotification: UserActionCreator.setNotification,
};

export const UsersTable = connect(mapStateToProps, mapDispatchToProps)(_UsersTable);

export default UsersTable;
